<template>
    <chart-table-wrapper
        v-if="chartOptions"
        :title="props.title"
        :chart-options="chartOptions"
        :chart-categories="categories"
        :x-axis-category="props.xAxisCategory"
        :suffix-unit="props.suffix"
        :additional-csv-export="additionalCsvExport"
    ><template #header>
        <slot name="customHeader" />
    </template>
    </chart-table-wrapper>
</template>

<script lang="ts" setup>
import ChartTableWrapper from '@/components/chart/ChartTableWrapper.vue';
import { Options, SeriesAreaOptions, SeriesColumnOptions } from 'highcharts';
import {
    ref, watch, useSlots, Slots, computed,
} from 'vue';
import { IFilters } from '@/interfaces/storageTracking/IFilters.ts';
import { chartColourArray, findCategoryColourCode } from '@/enums/storageTracking/Colours.ts';
import { getTooltipWrapper, getTooltipTableRow } from '@/helpers/charts/chartSettings.ts';
import { getAdditionalCsvExport } from '@/utils/storageTracking/ChartCSV.ts';

const slots: Slots = useSlots();

const props = defineProps<{
    rawData: Record<string, Record<string, number>>;
    title: string;
    yAxisTitle?: string;
    xAxisTitle?: string;
    xAxisCategory?: string;
    suffix?: string;
    filters?: IFilters;
    excludeLegend?: boolean;
}>();

const chartOptions = ref<Options>();

const hasCustomHeader = computed<boolean>(() => !!slots.customHeader);
const categories = computed(() => Object.keys(props.rawData) || []);
const additionalCsvExport = computed<Array<Array<string | number | undefined>>>(() => getAdditionalCsvExport(props.title, props.filters, props.suffix));

function createChartSeriesData (xAxisCategories: string[]): Array<SeriesAreaOptions | SeriesColumnOptions> {
    const data: Array<SeriesAreaOptions | SeriesColumnOptions> = [];

    const stackCategories: string[] = Array.from(
        new Set(Object.keys(props.rawData).flatMap(key => Object.keys(props.rawData[key]))),
    );

    stackCategories.forEach((stackCategory, index) => {
        let stackColor = findCategoryColourCode(stackCategory);

        if (!stackColor) {
            stackColor = chartColourArray[index % stackCategories.length];
        }

        const group: SeriesAreaOptions | SeriesColumnOptions = {
            type: 'column',
            stacking: 'normal',
            name: stackCategory,
            data: [],
            color: stackColor,
        };

        xAxisCategories.forEach((xCategory) => {
            const groupData = props.rawData[xCategory][stackCategory] ?? null;
            group.data?.push(groupData);
        });

        if (!group.data?.every(x => x === null)) data.push(group);
    });
    return data;
}

function updateChart (): void {
    const xAxisCategories: string[] = Object.keys(props.rawData);

    const customOptions: Options = {
        chart: {
            backgroundColor: 'transparent',
            spacingLeft: props.yAxisTitle && props.yAxisTitle !== '' ? 10 : 20,
            spacingRight: 0,
        },
        title: {
            text: hasCustomHeader.value ? '' : props.title,
        },
        xAxis: {
            categories: xAxisCategories,
            title: {
                text: props.xAxisTitle,
            },
        },
        yAxis: {
            title: {
                text: props.yAxisTitle,
                x: -10,
            },
        },
        legend: {
            enabled: !props.excludeLegend,
        },
        tooltip: {
            shared: false,
            split: false,
            formatter (this: Highcharts.TooltipFormatterContextObject): string {
                return getTooltipWrapper({
                    content: getTooltipTableRow({
                        label: this.point.series.name,
                        value: `${this.point.y ? Math.trunc(this.point.y).toLocaleString() : ''} ${props.suffix ?? ''}`,
                    }),
                    contentType: 'table',
                    header: typeof this.point.category === 'string' ? this.point.category : '',
                });
            },
        },
        series: createChartSeriesData(xAxisCategories),
        plotOptions: {
            column: {
                states: {
                    hover: {
                        enabled: false,
                    },
                },
                stickyTracking: false,
                point: {
                    events: {
                        mouseOver (this: Highcharts.Point): void {
                            this.setState('hover');
                            this.series.chart.tooltip.refresh(this);
                        },
                        mouseOut (this: Highcharts.Point): void {
                            this.setState('');
                        },
                    },
                },
            },
        },
    };

    chartOptions.value = customOptions;
}

watch(() => props, () => {
    updateChart();
}, { deep: true, immediate: true });

</script>
